/**
 * Search Visibility Quality Scorer
 *
 * Implements the Search Quality Score (SQS) framework to measure OSN+ visibility
 * in search results relative to pirate sites.
 *
 * Formula: SQS = 100 × (1 - (R-1)/N)^α × (1 - P/R)^β
 *
 * Where:
 * - R = Rank of first OSN+ appearance (1-50, or 51 if absent)
 * - P = Number of pirate results before OSN+
 * - N = Total number of results considered (default 50)
 * - α = Ranking sensitivity parameter (default 1.5)
 * - β = Pirate interference sensitivity parameter (default 1.0)
 */

import type { SearchResult } from '../lib/types';

export interface ClassifiedSearchResults {
  osnUrls: Array<{ url: string; rank: number; source?: 'exa' | 'firecrawl' | 'brave' }>;
  pirateUrls: Array<{ url: string; rank: number; domain: string; source?: 'exa' | 'firecrawl' | 'brave' }>;
  otherUrls: Array<{ url: string; rank: number; domain: string; source?: 'exa' | 'firecrawl' | 'brave' }>;
}

export interface VisibilityScore {
  score: number; // 0-100
  osnRank: number | null; // Position of first OSN+ (null if not found)
  piratesBeforeOsn: number;
  totalPirates: number;
  totalResults: number;
  interpretation: string;
  alpha: number;
  beta: number;
}

export interface ScorerConfig {
  osnDomains: string[]; // List of official OSN+ domains
  pirateDomains?: string[]; // Known pirate domains (optional, can use external classifier)
  alpha?: number; // Default 1.5
  beta?: number; // Default 1.0
  totalResults?: number; // Default 50
}

export class VisibilityScorer {
  private osnDomains: Set<string>;
  private pirateDomains: Set<string>;
  private alpha: number;
  private beta: number;
  private totalResults: number;

  constructor(config: ScorerConfig) {
    this.osnDomains = new Set(config.osnDomains.map(d => d.toLowerCase()));
    this.pirateDomains = new Set((config.pirateDomains || []).map(d => d.toLowerCase()));
    this.alpha = config.alpha ?? 1.5;
    this.beta = config.beta ?? 1.0;
    this.totalResults = config.totalResults ?? 50;
  }

  /**
   * Classify search results into OSN, Pirate, and Other categories
   */
  classifyResults(
    results: SearchResult[],
    pirateClassifier?: (domain: string, url: string) => boolean
  ): ClassifiedSearchResults {
    const classified: ClassifiedSearchResults = {
      osnUrls: [],
      pirateUrls: [],
      otherUrls: [],
    };

    for (const result of results) {
      const domainLower = result.domain.toLowerCase();

      // Check if OSN+ domain
      if (this.isOsnDomain(domainLower)) {
        classified.osnUrls.push({
          url: result.url,
          rank: result.rank,
          source: result.source,
        });
        continue;
      }

      // Check if pirate domain
      const isPirate = this.pirateDomains.has(domainLower) ||
                      (pirateClassifier && pirateClassifier(domainLower, result.url));

      if (isPirate) {
        classified.pirateUrls.push({
          url: result.url,
          rank: result.rank,
          domain: result.domain,
          source: result.source,
        });
      } else {
        classified.otherUrls.push({
          url: result.url,
          rank: result.rank,
          domain: result.domain,
          source: result.source,
        });
      }
    }

    return classified;
  }

  /**
   * Calculate Search Quality Score (SQS)
   */
  calculateScore(classified: ClassifiedSearchResults): VisibilityScore {
    const firstOsnRank = classified.osnUrls.length > 0
      ? Math.min(...classified.osnUrls.map(o => o.rank))
      : null;

    const totalPirates = classified.pirateUrls.length;

    // Count pirates before OSN+
    // If OSN+ is not found, ALL pirates are considered "before" it
    const piratesBeforeOsn = firstOsnRank !== null
      ? classified.pirateUrls.filter(p => p.rank < firstOsnRank).length
      : totalPirates;

    // Calculate score
    let score = 0;

    if (firstOsnRank !== null) {
      // R = rank (1-indexed)
      const R = firstOsnRank;
      // P = pirates before OSN
      const P = piratesBeforeOsn;
      // N = total results
      const N = this.totalResults;

      // Formula: SQS = 100 × (1 - (R-1)/N)^α × (1 - P/R)^β
      const rankFactor = Math.pow(1 - (R - 1) / N, this.alpha);
      const pirateFactor = Math.pow(1 - P / R, this.beta);

      score = 100 * rankFactor * pirateFactor;

      // Ensure score is within bounds
      score = Math.max(0, Math.min(100, score));
    } else {
      // OSN+ not found in results
      score = 0;
    }

    // Generate interpretation
    const interpretation = this.interpretScore(score, firstOsnRank, piratesBeforeOsn);

    return {
      score: Math.round(score * 100) / 100, // Round to 2 decimal places
      osnRank: firstOsnRank,
      piratesBeforeOsn,
      totalPirates,
      totalResults: this.totalResults,
      interpretation,
      alpha: this.alpha,
      beta: this.beta,
    };
  }

  /**
   * Provide human-readable interpretation of the score
   */
  private interpretScore(score: number, osnRank: number | null, piratesBeforeOsn: number): string {
    if (score === 0 || osnRank === null) {
      return '❌ Critical: OSN+ not visible in search results';
    }

    if (score >= 90) {
      return '✅ Excellent: OSN+ highly visible with minimal pirate interference';
    }

    if (score >= 80) {
      return '✅ Very Good: Strong OSN+ visibility with low pirate presence';
    }

    if (score >= 70) {
      return '⚠️ Good: OSN+ visible but some pirate interference detected';
    }

    if (score >= 50) {
      return '⚠️ Moderate: OSN+ present but significant pirate competition';
    }

    if (score >= 30) {
      return '🚨 Poor: Heavy pirate presence dominates results';
    }

    return '🚨 Critical: Severe pirate interference, OSN+ visibility severely compromised';
  }

  /**
   * Check if domain is an OSN+ official domain
   */
  private isOsnDomain(domain: string): boolean {
    const domainLower = domain.toLowerCase();

    // Check exact match
    if (this.osnDomains.has(domainLower)) {
      return true;
    }

    // Check if it's a subdomain of any OSN domain
    for (const osnDomain of this.osnDomains) {
      if (domainLower.endsWith('.' + osnDomain) || domainLower === osnDomain) {
        return true;
      }
    }

    return false;
  }

  /**
   * Update configuration parameters
   */
  updateConfig(config: Partial<ScorerConfig>): void {
    if (config.osnDomains) {
      this.osnDomains = new Set(config.osnDomains.map(d => d.toLowerCase()));
    }
    if (config.pirateDomains) {
      this.pirateDomains = new Set(config.pirateDomains.map(d => d.toLowerCase()));
    }
    if (config.alpha !== undefined) {
      this.alpha = config.alpha;
    }
    if (config.beta !== undefined) {
      this.beta = config.beta;
    }
    if (config.totalResults !== undefined) {
      this.totalResults = config.totalResults;
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): Required<ScorerConfig> {
    return {
      osnDomains: Array.from(this.osnDomains),
      pirateDomains: Array.from(this.pirateDomains),
      alpha: this.alpha,
      beta: this.beta,
      totalResults: this.totalResults,
    };
  }
}

/**
 * Default OSN+ domains (can be configured via settings)
 */
export const DEFAULT_OSN_DOMAINS = [
  'osnplus.com',
  'osn.com',
  'watch.osn.com',
  'www.osnplus.com',
  'www.osn.com',
];

/**
 * Create a scorer with default configuration
 */
export function createDefaultScorer(overrides?: Partial<ScorerConfig>): VisibilityScorer {
  return new VisibilityScorer({
    osnDomains: DEFAULT_OSN_DOMAINS,
    alpha: 1.5,
    beta: 1.0,
    totalResults: 50,
    ...overrides,
  });
}
